/**
 * Contact Service
 *
 * Service for submitting contact form messages.
 * Extends the base CrudService with proper error handling.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import {
  validateContactUs,
  type ContactUsInput,
} from '../validation/common.schemas';

// =============================================================================
// CONTACT SERVICE CLASS
// =============================================================================

/**
 * ContactService - Handles contact form submissions.
 */
export class ContactService extends CrudService<ContactUsInput> {
  constructor() {
    super({
      baseUrl: '/contact-me',
      enableCaching: false,
    });
  }

  /**
   * Submit contact form message
   *
   * POST /api/contact-me
   * Uses common.schemas.ts for client-side validation.
   */
  public async submit(data: ContactUsInput): Promise<void> {
    try {
      this.validateContactData(data);

      // NOTE: apiClient will prefix "/api/" so omit it
      const response = await apiClient.post('/contact-me', data);

      if (response.status !== 201) {
        throw new Error('Failed to send contact message');
      }
    } catch (error) {
      console.error('Failed to send contact message:', error);
      rethrowTyped(error, 'Unable to send message. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------------------------

  /**
   * Validate contact payload using Zod schema from common.schemas.ts.
   */
  protected validateContactData(data: ContactUsInput): void {
    try {
      validateContactUs(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't send your message", error, {
          originalData: data,
          details: { service: 'ContactService' },
        });
      }

      throw new InvalidDataError(
        "Couldn't send your message: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'ContactService' },
        }
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default contact service instance
 * Pre-configured and ready to use throughout the application
 */
export const contactService = new ContactService();
